import React, { useState, useEffect, useCallback } from 'react';
import {
  khoiAPI,
  lopHocAPI,
  monHocAPI,
  phanCongAPI,
  tkbAPI,
  blockedCellAPI,
  fixedCellAPI
} from '../services/api';
import { FiAlertTriangle, FiCheck, FiX, FiChevronRight, FiLoader, FiCheckCircle } from 'react-icons/fi';

// ─── helpers ────────────────────────────────────────────────────────────────

/** Trả về khoi liền kề cao hơn trong danh sách đã sắp xếp theo thu_tu */
function getNextKhoi(khoiId, sortedKhois) {
  const idx = sortedKhois.findIndex(k => k.khoi_id === khoiId);
  if (idx === -1 || idx === sortedKhois.length - 1) return null;
  return sortedKhois[idx + 1];
}

// ─── Component ───────────────────────────────────────────────────────────────

const ChuyenNamHocModal = ({ onClose, onDone }) => {
  const [step, setStep] = useState(1); // 1=warning, 2=preview, 3=processing, 4=done

  // data
  const [khois, setKhois] = useState([]);
  const [phanCongs, setPhanCongs] = useState([]);
  const [monHocs, setMonHocs] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [dataError, setDataError] = useState('');

  // preview analysis
  const [deleteClasses, setDeleteClasses] = useState([]); // { lop, khoi }
  const [promoteClasses, setPromoteClasses] = useState([]); // { lop, fromKhoi, toKhoi }
  const [unchangedClasses, setUnchangedClasses] = useState([]); // lop without khoi

  // processing
  const [progress, setProgress] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [doneError, setDoneError] = useState('');

  // ── Load data ──────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoadingData(true);
    setDataError('');
    try {
      const [khoiRes, lopRes, pcRes, monRes] = await Promise.all([
        khoiAPI.getAll(),
        lopHocAPI.getAll(),
        phanCongAPI.getAll(),
        monHocAPI.getAll()
      ]);
      const khoiData = khoiRes.data?.success ? (khoiRes.data.data || []) : [];
      const lopData = lopRes.data?.success ? (lopRes.data.data || []) : [];
      const pcData = pcRes.data?.success ? (pcRes.data.data || []) : [];
      const monData = monRes.data?.success ? (monRes.data.data || []) : [];

      setKhois(khoiData);
      setPhanCongs(pcData);
      setMonHocs(monData);

      // ── Analyse ──
      const sorted = [...khoiData].sort((a, b) => a.thu_tu - b.thu_tu);
      const lastKhoi = sorted[sorted.length - 1];

      const dels = [];
      const prom = [];
      const unch = [];

      for (const lop of lopData) {
        if (!lop.khoi_id) {
          unch.push(lop);
          continue;
        }
        const kh = khoiData.find(k => k.khoi_id === lop.khoi_id);
        if (!kh) { unch.push(lop); continue; }

        if (kh.khoi_id === lastKhoi?.khoi_id) {
          dels.push({ lop, khoi: kh });
        } else {
          const next = getNextKhoi(lop.khoi_id, sorted);
          prom.push({ lop, fromKhoi: kh, toKhoi: next });
        }
      }

      setDeleteClasses(dels);
      setPromoteClasses(prom);
      setUnchangedClasses(unch);
    } catch (err) {
      setDataError('Không thể tải dữ liệu. Vui lòng thử lại.');
    } finally {
      setLoadingData(false);
    }
  }, []);

  // ── Execute ────────────────────────────────────────────────────────────────
  const addLog = (msg, type = 'info') => {
    setProgress(prev => [...prev, { msg, type, ts: Date.now() }]);
  };

  const runPromotion = async () => {
    setProcessing(true);
    setProgress([]);
    setDoneError('');

    try {
      // ── 1. Xóa TKB ──
      addLog('Đang xóa toàn bộ thời khóa biểu...', 'info');
      await tkbAPI.deleteAll();
      addLog('✓ Đã xóa toàn bộ TKB.', 'ok');

      // ── 2. Xóa ô chặn & ô cố định ──
      addLog('Đang xóa ô bị chặn và ô cố định...', 'info');
      await blockedCellAPI.deleteAll();
      await fixedCellAPI.deleteAll();
      addLog('✓ Đã xóa ô bị chặn và ô cố định.', 'ok');

      // ── 3. Xóa lớp cuối cấp ──
      if (deleteClasses.length > 0) {
        addLog(`Đang xóa ${deleteClasses.length} lớp cuối cấp (tốt nghiệp)...`, 'info');
        for (const { lop } of deleteClasses) {
          // Xóa phân công của lớp này
          const lpcPc = phanCongs.filter(pc => pc.lop_id === lop.lop_id);
          for (const pc of lpcPc) {
            try { await phanCongAPI.delete(pc.pc_id); } catch { /* ignore */ }
          }
          // Xóa lớp
          await lopHocAPI.delete(lop.lop_id);
          addLog(`  ✓ Đã xóa lớp ${lop.ten_lop}`, 'ok');
        }
      }

      // ── 4. Lên lớp & remap phân công ──
      if (promoteClasses.length > 0) {
        addLog(`Đang thực hiện lên lớp cho ${promoteClasses.length} lớp...`, 'info');

        for (const { lop, toKhoi } of promoteClasses) {
          // Remap phân công: tìm môn cùng tên trong khối mới
          const lopPcs = phanCongs.filter(pc => pc.lop_id === lop.lop_id);
          for (const pc of lopPcs) {
            const oldMon = monHocs.find(m => m.mon_id === pc.mon_id);
            if (!oldMon) {
              try { await phanCongAPI.delete(pc.pc_id); } catch { /* ignore */ }
              continue;
            }
            if (toKhoi) {
              // Tìm môn cùng tên ở khối mới
              const newMon = monHocs.find(
                m => m.ten_mon === oldMon.ten_mon && m.khoi_id === toKhoi.khoi_id
              );
              if (newMon && newMon.mon_id !== oldMon.mon_id) {
                // Xóa phân công cũ, tạo phân công mới
                try { await phanCongAPI.delete(pc.pc_id); } catch { /* ignore */ }
                try {
                  await phanCongAPI.create({
                    gvId: pc.gv_id,
                    lopId: lop.lop_id,
                    monId: newMon.mon_id
                  });
                } catch { /* ignore duplicate */ }
              } else if (!newMon) {
                // Không có môn tương ứng → xóa phân công
                try { await phanCongAPI.delete(pc.pc_id); } catch { /* ignore */ }
              }
              // Nếu newMon.mon_id === oldMon.mon_id (môn không gắn khối) → giữ nguyên
            } else {
              // Không có khối tiếp theo → xóa phân công
              try { await phanCongAPI.delete(pc.pc_id); } catch { /* ignore */ }
            }
          }

          // Cập nhật khối của lớp
          await lopHocAPI.update(lop.lop_id, {
            tenLop: lop.ten_lop,
            khoiId: toKhoi ? toKhoi.khoi_id : null,
            moTa: lop.mo_ta || ''
          });

          const newName = toKhoi ? `(${toKhoi.ten_khoi})` : '';
          addLog(`  ✓ Đã lên lớp: ${lop.ten_lop} → ${newName}`, 'ok');
        }
      }

      addLog('', 'separator');
      addLog('🎉 Hoàn thành! Hệ thống đã sẵn sàng cho năm học mới.', 'done');
      setStep(4);
    } catch (err) {
      addLog('❌ Có lỗi xảy ra trong quá trình thực hiện.', 'error');
      setDoneError(err?.response?.data?.message || err.message || 'Lỗi không xác định.');
    } finally {
      setProcessing(false);
    }
  };

  // Load data khi mở modal (bước 1 → 2)
  useEffect(() => {
    if (step === 2) loadData();
  }, [step, loadData]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '620px',
        maxHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: 'linear-gradient(135deg, #fff5f5 0%, #fff 100%)',
          borderRadius: '16px 16px 0 0'
        }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '10px',
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0
          }}>
            <FiAlertTriangle size={20} color="#fff" />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '700', color: '#1e293b' }}>
              Chuyển sang năm học mới
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
              Bước {step} / {step < 4 ? '3' : '3'}
              {step === 1 && ' — Cảnh báo'}
              {step === 2 && ' — Xem trước thay đổi'}
              {step === 3 && ' — Đang thực hiện'}
              {step === 4 && ' — Hoàn thành'}
            </p>
          </div>
          {(step === 1 || step === 4) && (
            <button onClick={onClose} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '4px', color: '#94a3b8', borderRadius: '6px'
            }}>
              <FiX size={20} />
            </button>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* ── Bước 1: Cảnh báo ── */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: '12px', padding: '16px',
                display: 'flex', gap: '12px'
              }}>
                <FiAlertTriangle size={20} color="#ef4444" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{ margin: '0 0 8px', fontWeight: '700', color: '#dc2626', fontSize: '14px' }}>
                    ⚠️ Hành động này KHÔNG THỂ hoàn tác!
                  </p>
                  <p style={{ margin: 0, fontSize: '13.5px', color: '#7f1d1d', lineHeight: '1.6' }}>
                    Trước khi tiếp tục, hãy <strong>xuất TKB ra Excel</strong> để lưu lại dữ liệu năm học hiện tại.
                    Vào trang <strong>Kết quả TKB</strong> → nhấn nút <strong>Xuất Excel</strong>.
                  </p>
                </div>
              </div>

              <div>
                <p style={{ margin: '0 0 10px', fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                  Hệ thống sẽ thực hiện các bước sau:
                </p>
                {[
                  { icon: '🗑️', label: 'Xóa toàn bộ thời khóa biểu hiện tại' },
                  { icon: '🔓', label: 'Xóa toàn bộ ô cố định và ô bị chặn' },
                  { icon: '🎓', label: 'Xóa các lớp cuối cấp (tốt nghiệp)' },
                  { icon: '⬆️', label: 'Tự động lên lớp: lớp 10 → 11, lớp 11 → 12, …' },
                  { icon: '🔗', label: 'Kéo theo phân công cũ (GV dạy Toán 10A1 → tiếp tục dạy Toán 11A1)' },
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px',
                    padding: '8px 12px', marginBottom: '6px',
                    background: '#f8fafc', borderRadius: '8px',
                    border: '1px solid #e2e8f0', fontSize: '13.5px', color: '#374151'
                  }}>
                    <span style={{ fontSize: '16px', flexShrink: 0 }}>{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>

              <div style={{
                background: '#fffbeb', border: '1px solid #fde68a',
                borderRadius: '10px', padding: '12px 16px',
                fontSize: '13px', color: '#92400e'
              }}>
                💡 Sau khi hoàn tất, bạn sẽ cần <strong>thêm lớp 10 mới</strong>, điều chỉnh phân công và xếp lại thời khóa biểu.
              </div>
            </div>
          )}

          {/* ── Bước 2: Preview ── */}
          {step === 2 && (
            <div>
              {loadingData ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  <FiLoader size={28} style={{ animation: 'spin 1s linear infinite', marginBottom: '10px' }} />
                  <p>Đang tải dữ liệu...</p>
                </div>
              ) : dataError ? (
                <div style={{ color: '#dc2626', padding: '20px', textAlign: 'center' }}>
                  <p>{dataError}</p>
                  <button className="btn btn-secondary" onClick={loadData} style={{ marginTop: '8px' }}>
                    Thử lại
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {khois.length < 2 && (
                    <div style={{
                      background: '#fef2f2', borderRadius: '10px', padding: '14px',
                      border: '1px solid #fecaca', color: '#dc2626', fontSize: '13.5px'
                    }}>
                      ⚠️ Cần có <strong>ít nhất 2 khối học</strong> để thực hiện lên lớp tự động.
                      Vui lòng thêm khối học tại trang <strong>Khối học</strong>.
                    </div>
                  )}

                  {/* Lớp cuối cấp – sẽ xóa */}
                  <Section
                    title={`🎓 Lớp cuối cấp sẽ xóa (${deleteClasses.length} lớp)`}
                    color="#ef4444"
                    bg="#fef2f2"
                    border="#fecaca"
                    empty="Không có lớp cuối cấp nào."
                  >
                    {deleteClasses.map(({ lop, khoi }) => (
                      <Row key={lop.lop_id} left={lop.ten_lop} right={`Khối: ${khoi.ten_khoi}`} color="#dc2626" tag="Xóa" tagColor="#fee2e2" tagText="#dc2626" />
                    ))}
                  </Section>

                  {/* Lớp được lên lớp */}
                  <Section
                    title={`⬆️ Lớp sẽ lên lớp (${promoteClasses.length} lớp)`}
                    color="#0284c7"
                    bg="#f0f9ff"
                    border="#bae6fd"
                    empty="Không có lớp nào để lên lớp."
                  >
                    {promoteClasses.map(({ lop, fromKhoi, toKhoi }) => (
                      <Row
                        key={lop.lop_id}
                        left={lop.ten_lop}
                        right={<><span style={{ color: '#94a3b8' }}>{fromKhoi.ten_khoi}</span> <FiChevronRight size={13} /> <span style={{ color: '#0284c7', fontWeight: '600' }}>{toKhoi?.ten_khoi || '?'}</span></>}
                        tag="Lên lớp" tagColor="#e0f2fe" tagText="#0284c7"
                      />
                    ))}
                  </Section>

                  {/* Lớp không thay đổi */}
                  {unchangedClasses.length > 0 && (
                    <Section
                      title={`📌 Lớp không thay đổi (${unchangedClasses.length} lớp, không thuộc khối nào)`}
                      color="#64748b" bg="#f8fafc" border="#e2e8f0"
                    >
                      {unchangedClasses.map(lop => (
                        <Row key={lop.lop_id} left={lop.ten_lop} right="Không có khối" tag="Giữ nguyên" tagColor="#f1f5f9" tagText="#64748b" />
                      ))}
                    </Section>
                  )}

                  <div style={{
                    background: '#f0fdf4', borderRadius: '10px', padding: '12px 16px',
                    border: '1px solid #bbf7d0', fontSize: '13px', color: '#166534'
                  }}>
                    🔗 Phân công của giáo viên sẽ được tự động chuyển sang môn học cùng tên ở khối mới
                    (nếu tồn tại). Phân công không tìm được môn tương ứng sẽ bị xóa.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Bước 3: Processing ── */}
          {step === 3 && (
            <div>
              <div style={{
                background: '#f8fafc', borderRadius: '10px', padding: '14px 16px',
                border: '1px solid #e2e8f0', maxHeight: '320px', overflowY: 'auto',
                fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.7'
              }}>
                {progress.length === 0 ? (
                  <span style={{ color: '#94a3b8' }}>Đang khởi động...</span>
                ) : (
                  progress.map((p, i) => (
                    p.type === 'separator'
                      ? <hr key={i} style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '6px 0' }} />
                      : <div key={i} style={{
                          color: p.type === 'ok' ? '#16a34a' : p.type === 'error' ? '#dc2626' : p.type === 'done' ? '#6366f1' : '#374151'
                        }}>{p.msg}</div>
                  ))
                )}
                {processing && (
                  <div style={{ color: '#6366f1', marginTop: '4px' }}>
                    <FiLoader size={13} style={{ animation: 'spin 1s linear infinite', marginRight: '6px', verticalAlign: 'middle' }} />
                    Đang xử lý...
                  </div>
                )}
                {doneError && (
                  <div style={{ color: '#dc2626', marginTop: '8px' }}>
                    <strong>Lỗi:</strong> {doneError}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Bước 4: Hoàn thành ── */}
          {step === 4 && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 8px 24px rgba(16,185,129,0.35)'
              }}>
                <FiCheckCircle size={32} color="#fff" />
              </div>
              <h3 style={{ margin: '0 0 8px', color: '#1e293b', fontSize: '20px' }}>
                Chuyển năm học thành công!
              </h3>
              <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '14px', lineHeight: '1.6' }}>
                Hệ thống đã sẵn sàng cho năm học mới.<br />
                Tiếp theo hãy thêm lớp 10 mới, kiểm tra phân công và xếp lại thời khóa biểu.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <StepHint num={1} text="Thêm lớp 10 mới" color="#06b6d4" />
                <StepHint num={2} text="Kiểm tra phân công" color="#10b981" />
                <StepHint num={3} text="Xếp lại TKB" color="#6366f1" />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          background: '#fafafa',
          borderRadius: '0 0 16px 16px'
        }}>
          {step === 1 && (
            <>
              <button onClick={onClose} style={btnStyle('secondary')}>Hủy</button>
              <button onClick={() => setStep(2)} style={btnStyle('warning')}>
                Tôi đã xuất Excel, tiếp tục <FiChevronRight />
              </button>
            </>
          )}
          {step === 2 && !loadingData && !dataError && (
            <>
              <button onClick={() => setStep(1)} style={btnStyle('secondary')}>Quay lại</button>
              <button
                onClick={() => { setStep(3); runPromotion(); }}
                disabled={khois.length < 2}
                style={btnStyle('danger', khois.length < 2)}
              >
                Xác nhận thực hiện
              </button>
            </>
          )}
          {step === 2 && (loadingData || dataError) && (
            <button onClick={() => setStep(1)} style={btnStyle('secondary')}>Quay lại</button>
          )}
          {step === 3 && !processing && (
            <button onClick={doneError ? onClose : onDone} style={btnStyle(doneError ? 'secondary' : 'primary')}>
              {doneError ? 'Đóng' : 'Hoàn thành'}
            </button>
          )}
          {step === 4 && (
            <button onClick={onDone} style={btnStyle('primary')}>
              <FiCheck /> Đóng và làm mới trang
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function Section({ title, color, bg, border, empty, children }) {
  const count = React.Children.count(children);
  return (
    <div style={{ borderRadius: '10px', border: `1px solid ${border}`, overflow: 'hidden' }}>
      <div style={{ background: bg, padding: '8px 14px', borderBottom: `1px solid ${border}` }}>
        <span style={{ fontWeight: '600', fontSize: '13.5px', color }}>{title}</span>
      </div>
      <div style={{ padding: count ? '4px 8px' : '10px 14px' }}>
        {count === 0
          ? <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>{empty}</p>
          : children}
      </div>
    </div>
  );
}

function Row({ left, right, tag, tagColor, tagText }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '5px 6px', fontSize: '13px', borderRadius: '6px'
    }}>
      <span style={{ flex: '0 0 120px', fontWeight: '600', color: '#374151' }}>{left}</span>
      <span style={{ flex: 1, color: '#64748b', display: 'flex', alignItems: 'center', gap: '4px' }}>{right}</span>
      {tag && (
        <span style={{
          background: tagColor, color: tagText, fontSize: '11px', fontWeight: '600',
          padding: '2px 8px', borderRadius: '20px', flexShrink: 0
        }}>{tag}</span>
      )}
    </div>
  );
}

function StepHint({ num, text, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '8px 14px', background: '#f8fafc',
      borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px'
    }}>
      <span style={{
        width: '22px', height: '22px', borderRadius: '50%',
        background: color, color: '#fff', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: '12px', fontWeight: '700', flexShrink: 0
      }}>{num}</span>
      <span style={{ color: '#374151' }}>{text}</span>
    </div>
  );
}

function btnStyle(type, disabled = false) {
  const base = {
    padding: '9px 18px', borderRadius: '8px', border: 'none',
    fontSize: '14px', fontWeight: '600', cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex', alignItems: 'center', gap: '6px',
    opacity: disabled ? 0.5 : 1, transition: 'all 0.15s'
  };
  if (type === 'primary')   return { ...base, background: '#6366f1', color: '#fff' };
  if (type === 'danger')    return { ...base, background: '#ef4444', color: '#fff' };
  if (type === 'warning')   return { ...base, background: '#f59e0b', color: '#fff' };
  if (type === 'secondary') return { ...base, background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0' };
  return base;
}

export default ChuyenNamHocModal;
